import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '../components/Button';
import Card from '../components/Card';
import NavBar from '../components/NavBar';
import EducationalBanner from '../components/EducationalBanner';
import { useFirstVisit } from '../hooks/useFirstVisit';

function FocusBubble() {
  const navigate = useNavigate();
  const [timeLeft, setTimeLeft] = useState(20 * 60); // 20 minutes in seconds
  const [isActive, setIsActive] = useState(false);
  const [offlineTask, setOfflineTask] = useState('');
  const [tasksCompleted, setTasksCompleted] = useState([]);
  const { isFirstVisit, setVisited } = useFirstVisit('focus');
  const [showEducationalBanner, setShowEducationalBanner] = useState(false);

  const suggestedTasks = [
    "Take a 10-minute walk outside",
    "Write in a journal for 5 minutes", 
    "Practice deep breathing",
    "Organize your workspace",
    "Read a chapter of a book",
    "Do some light stretching"
  ];

  // Scroll to top on mount and check for first visit
  useEffect(() => {
    window.scrollTo(0, 0);
    
    if (isFirstVisit) {
      setShowEducationalBanner(true);
    }
  }, [isFirstVisit]);

  useEffect(() => {
    let interval = null;
    if (isActive && timeLeft > 0) {
      interval = setInterval(() => {
        setTimeLeft(timeLeft => timeLeft - 1);
      }, 1000);
    } else if (timeLeft === 0) {
      setIsActive(false);
      alert('Focus session complete! Great job disconnecting.');
      navigate('/social');
    }
    return () => clearInterval(interval);
  }, [isActive, timeLeft, navigate]);

  function formatTime(seconds) {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  }

  function startFocus() {
    setIsActive(true);
  }

  function stopFocus() {
    setIsActive(false);
  }

  function completeTask(task) {
    if (!tasksCompleted.includes(task)) {
      setTasksCompleted([...tasksCompleted, task]);
    }
  }

  // Handle closing the educational banner
  const handleCloseBanner = () => {
    setShowEducationalBanner(false);
    setVisited();
  };

  return (
    <div style={{ maxWidth: '480px', margin: 'auto', padding: '20px', backgroundColor: '#f5f7fa', minHeight: '100vh', paddingTop: '60px' }}>
      <NavBar title="🔮 Focus Bubble" />
      
      {/* Educational Banner - Only shows on first visit */}
      {showEducationalBanner && (
        <EducationalBanner 
          feature="focus" 
          onDismiss={handleCloseBanner} 
        />
      )}
      
      <Card style={{ textAlign: 'center' }}>
        <h1 style={{ color: '#2c3e50', marginBottom: '8px', fontWeight: '700' }}>🔮 Focus Bubble</h1>
        <p style={{ color: '#64748b', margin: '0 0 16px 0' }}>
          Enter your digital detox bubble and focus on offline activities
        </p>
      </Card>

      <Card style={{ textAlign: 'center', padding: '40px' }}>
        <div style={{
          width: '150px',
          height: '150px',
          backgroundColor: isActive ? '#4caf50' : '#e0e0e0',
          borderRadius: '50%',
          margin: '0 auto 24px auto',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '36px',
          fontWeight: 'bold',
          color: 'white',
          transition: 'transform 0.3s ease',
          boxShadow: isActive ? '0 0 20px rgba(76, 175, 80, 0.5)' : 'none'
        }}>
          {formatTime(timeLeft)}
        </div>

        {isActive && (
          <div style={{ marginBottom: '16px' }}>
            <div style={{ fontSize: '18px', color: '#4caf50', marginBottom: '8px' }}>
              🔇 Silent Mode Active
            </div>
            <div style={{ fontSize: '14px', color: '#7f8c8d' }}>
              All notifications are paused
            </div>
          </div>
        )}

        <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
          <Button 
            onClick={startFocus}
            disabled={isActive}
            style={{ backgroundColor: isActive ? '#4caf50' : '#4361ee', margin: 0 }}
          >
            {isActive ? 'Focus Active' : 'Start Focus'}
          </Button>
          
          <Button 
            onClick={stopFocus}
            disabled={!isActive}
            style={{ backgroundColor: '#f44336', margin: 0 }}
          >
            End Session
          </Button>
        </div>
      </Card>

      <Card>
        <h3 style={{ color: '#2c3e50', marginBottom: '16px', fontWeight: '600' }}>📝 Offline Activities</h3>
        <p style={{ color: '#64748b', fontSize: '14px', marginBottom: '16px' }}>
          Make your focus time meaningful with these activities:
        </p>

        <div style={{ marginBottom: '16px' }}>
          <input
            type="text"
            placeholder="Add your own offline activity..."
            value={offlineTask}
            onChange={(e) => setOfflineTask(e.target.value)}
            style={{
              width: '100%',
              padding: '12px',
              fontSize: '14px',
              border: '1px solid #ddd',
              borderRadius: '8px',
              marginBottom: '8px'
            }}
          />
          <Button 
            onClick={() => {
              if (offlineTask.trim()) {
                completeTask(offlineTask);
                setOfflineTask('');
              }
            }}
            style={{ width: '100%', margin: 0, fontSize: '14px' }}
          >
            Add & Complete Activity
          </Button>
        </div>

        <div>
          <h4 style={{ color: '#2c3e50', marginBottom: '12px', fontWeight: '600' }}>Suggested Activities:</h4>
          {suggestedTasks.map((task, index) => (
            <div key={index} style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              padding: '8px 0',
              borderBottom: index < suggestedTasks.length - 1 ? '1px solid #f0f0f0' : 'none'
            }}>
              <span style={{ 
                fontSize: '14px', 
                color: tasksCompleted.includes(task) ? '#4caf50' : '#2c3e50',
                textDecoration: tasksCompleted.includes(task) ? 'line-through' : 'none'
              }}>
                {tasksCompleted.includes(task) ? '✅' : '○'} {task}
              </span>
              {!tasksCompleted.includes(task) && (
                <Button 
                  onClick={() => completeTask(task)}
                  style={{ 
                    margin: 0, 
                    padding: '4px 8px', 
                    fontSize: '12px',
                    backgroundColor: '#2196f3'
                  }}
                >
                  Done
                </Button>
              )}
            </div>
          ))}
        </div>
      </Card>

      {tasksCompleted.length > 0 && (
        <Card style={{ backgroundColor: '#e8f5e8', border: '2px solid #4caf50' }}>
          <div style={{ textAlign: 'center' }}>
            <h3 style={{ color: '#2e7d32', margin: '0 0 8px 0', fontWeight: '600' }}>
              🎉 Great Job!
            </h3>
            <p style={{ color: '#388e3c', margin: '0 0 8px 0' }}>
              You&apos;ve completed {tasksCompleted.length} offline activities
            </p>
            <div style={{ fontSize: '14px', color: '#4caf50' }}>
              +{tasksCompleted.length * 10} XP earned for mindful living!
            </div>
          </div>
        </Card>
      )}

      <div style={{ marginTop: '24px', display: 'flex', gap: '8px' }}>
        <Button onClick={() => navigate('/social')} style={{ flex: 1, backgroundColor: '#9c27b0' }}>
          ← Social Feed
        </Button>
        <Button onClick={() => navigate('/peer')} style={{ flex: 1, backgroundColor: '#ff5722' }}>
          Peer Support →
        </Button>
      </div>
    </div>
  );
}

export default FocusBubble;
